import { SOCKET_EVENT_NAMES } from '@/constants/socket.events';
import { notify } from '@/helpers/index';
import { teamMateStore } from '@/state/TeammateStore';
import { SocketRequest } from '@/config/socket';
import {
  Invitation,
  TeamMate,
  SendTeammateInviteRequest,
  SendTeammateInviteResponse,
  ResendInviteRequest,
  DeleteInviteRequest,
  DeleteInviteResponse,
  DeleteTeammateRequest,
  TeammateStatusResponse,
  TeammateRoleResponse,
} from '@/types/teammate.types';
import { useNavigate } from 'react-router-dom';

export const useTeammate = () => {
  const navigate = useNavigate();
  const sendTeamMateInvite = async (invitations: any) => {
    teamMateStore.clearEmails();
    teamMateStore.setSendingInvite(true);
    try {
      const response = await SocketRequest<
        SendTeammateInviteRequest,
        SendTeammateInviteResponse
      >(SOCKET_EVENT_NAMES.INVITE_TEAMMATES, { data: { invitations } });

      if (response?.status === 200) {
        teamMateStore.addInvite(response?.data?.invitations || []);
        notify('success', 'Invites sent successfully');
        navigate(-1);
      } else {
        throw new Error(
          (response?.error as string) || 'Failed to send invites',
        );
      }
    } catch (error: any) {
      notify('error', error?.message || 'Something went wrong');
    } finally {
      teamMateStore.setSendingInvite(false);
    }
  };

  const resendInvite = async (email: string, role: string) => {
    teamMateStore.setSendingInvite(true);
    try {
      const response = await SocketRequest<
        ResendInviteRequest,
        DeleteInviteResponse
      >(SOCKET_EVENT_NAMES.RESEND_INVITE, { data: { email, role } });
      if (response?.status === 200) {
        notify('success', 'Invite resent successfully');
      } else {
        throw new Error('Failed to resend invite');
      }
    } catch (error) {
      notify('error', 'Something went wrong');
    } finally {
      teamMateStore.setSendingInvite(false);
    }
  };

  const deleteInvite = async (inviteId: string) => {
    teamMateStore.setSendingInvite(true);
    try {
      const response = await SocketRequest<
        DeleteInviteRequest,
        DeleteInviteResponse
      >(SOCKET_EVENT_NAMES.DELETE_INVITE, { data: { invite_id: inviteId } });
      if (response?.status === 200 && response?.data) {
        const deletedInviteId = response?.data?._id;
        const copyOfInvites = [...teamMateStore.invites];
        const inviteIndex = teamMateStore.invites?.findIndex(
          invite => invite?._id === deletedInviteId,
        );
        copyOfInvites.splice(inviteIndex, 1);
        teamMateStore.setInvites(copyOfInvites);
        notify('success', 'Invite deleted successfully');
        await getInvites();
      } else {
        throw new Error('Failed to delete invite');
      }
    } catch (error) {
      notify('error', 'Something went wrong');
    } finally {
      teamMateStore.setSendingInvite(false);
    }
  };

  const deleteTeamMate = async (userId: string) => {
    teamMateStore.setSendingInvite(true);
    try {
      const response = await SocketRequest<
        DeleteTeammateRequest,
        DeleteInviteResponse
      >(SOCKET_EVENT_NAMES.DELETE_TEAMMATE, { data: { userId } });
      if (response?.status === 200) {
        const copyOfTeammates = [...teamMateStore.teamMates];
        const teammateIndex = teamMateStore.teamMates?.findIndex(
          teammate => teammate?.user?._id === userId,
        );
        copyOfTeammates?.splice(teammateIndex, 1);
        teamMateStore.setTeamMates(copyOfTeammates);
        notify('success', 'Teammate deleted successfully');
      } else {
        throw new Error('Failed to delete teammate');
      }
    } catch (error) {
      notify('error', 'Something went wrong');
    } finally {
      teamMateStore.setSendingInvite(false);
    }
  };

  const getTeamMates = async (search?: string) => {
    teamMateStore.setFetchingTeamMates(true);
    try {
      const response = await SocketRequest<{ search?: string }, TeamMate[]>(
        SOCKET_EVENT_NAMES.GET_TEAMMATES,
        { data: { search } },
      );
      if (response?.data) {
        teamMateStore.setTeamMates([...(response?.data || [])]);
      }
    } catch (error) {
      notify('error', 'Failed to fetch teammates');
    } finally {
      teamMateStore.setFetchingTeamMates(false);
    }
  };
  const getInvites = async (search?: string) => {
    teamMateStore.setFetchingInvites(true);
    try {
      const response = await SocketRequest<{ search?: string }, Invitation[]>(
        SOCKET_EVENT_NAMES.GET_INVITES,
        { data: { search } },
      );
      if (response?.data) {
        teamMateStore.setInvites(response?.data);
      }
    } catch (error) {
      notify('error', 'Failed to fetch invites');
    } finally {
      teamMateStore.setFetchingInvites(false);
    }
  };
  const teammateStatus = async (data: any) => {
    teamMateStore.updateTeammateStatus(true);
    try {
      const response = await SocketRequest<any, TeammateStatusResponse>(
        SOCKET_EVENT_NAMES.UPDATE_TEAMMATE_STATUS,
        { data },
      );
      if (response?.status === 200 && response?.data) {
        notify('success', 'Status updated successfully');
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      notify('error', 'Something went wrong');
    } finally {
      teamMateStore.updateTeammateStatus(false);
    }
  };
  const upateTeammateRole = async (data: any) => {
    teamMateStore.updateTeammateRole(true);
    try {
      const response = await SocketRequest<any, TeammateRoleResponse>(
        SOCKET_EVENT_NAMES.UPDATE_TEAMMATE_ROLE,
        { data },
      );
      if (response?.status === 200 && response?.data) {
        getTeamMates();
        notify('success', 'Role updated successfully');
        teamMateStore.setTeamMates(response?.data);
      } else {
        throw new Error('Failed to update role');
      }
    } catch (error) {
      notify('error', 'Something went wrong');
    } finally {
      teamMateStore.updateTeammateRole(false);
    }
  };

  return {
    sendTeamMateInvite,
    getTeamMates,
    getInvites,
    resendInvite,
    deleteTeamMate,
    deleteInvite,
    teammateStatus,
    upateTeammateRole,
  } as const;
};
